import React, {useCallback, useState} from 'react';
import {Button, Icon, Text} from '@gravity-ui/uikit';
import {CloudArrowUpIn} from '@gravity-ui/icons';
import styles from './FileUploadDropzone.module.scss';
import classNames from 'classnames';
import {formatFileSize} from '../../lib';

interface FileUploadDropzoneProps {
  onFileSelect: (file: File) => void;
  accept?: string;
  maxSize?: number;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
}

export const FileUploadDropzone = ({
  onFileSelect,
  accept = '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png',
  maxSize = 25 * 1024 * 1024,
  isLoading = false,
  disabled = false,
  className,
}: FileUploadDropzoneProps) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Функция для форматирования списка поддерживаемых форматов
  const getFormattedAcceptTypes = useCallback(() => {
    const extensions = accept.split(',').map((ext) => ext.trim().toUpperCase().replace('.', ''));
    return extensions.join(', ');
  }, [accept]);

  const validateFile = useCallback(
    (file: File): boolean => {
      setError(null);

      if (file.size > maxSize) {
        setError(`Файл слишком большой. Максимальный размер: ${Math.round(maxSize / 1024 / 1024)}MB`);
        return false;
      }

      return true;
    },
    [maxSize],
  );

  const handleFileSelect = useCallback(
    (file: File) => {
      if (validateFile(file)) {
        onFileSelect(file);
      }
    },
    [onFileSelect, validateFile],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      if (disabled || isLoading) return;

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [disabled, isLoading, handleFileSelect],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled && !isLoading) {
        setIsDragOver(true);
      }
    },
    [disabled, isLoading],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleClick = useCallback(() => {
    if (disabled || isLoading) return;

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    };
    input.click();
  }, [accept, disabled, isLoading, handleFileSelect]);

  return (
    <div
      className={classNames(styles.dropzone, {[styles.dragOver]: isDragOver}, {[styles.disabled]: disabled}, className)}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={handleClick}>
      <Icon data={CloudArrowUpIn} size={40} className={styles.icon} />

      <div className={styles.content}>
        <div className={styles.textGroup}>
          <Text variant="body-2" color="secondary">
            Перетащите сюда файл для загрузки
          </Text>
          <Text variant="body-2" color="secondary">
            или
          </Text>
        </div>

        <Button view="normal-contrast" size="l" disabled={disabled || isLoading}>
          {isLoading ? 'Загружается...' : 'Выберите файл'}
        </Button>
      </div>

      <Text variant="caption-2" color="secondary">
        Файлы в формате {getFormattedAcceptTypes()} до {formatFileSize(maxSize)}
      </Text>

      {error && (
        <Text variant="caption-2" color="danger">
          {error}
        </Text>
      )}
    </div>
  );
};
